import React, { createContext, useContext, useState, useCallback } from 'react';
import MediaDialog from './MediaDialog';
import type { MediaFile, MediaImage } from '../../models/media';

interface MediaDialogContextType {
  openMediaDialog: (options?: {
    onSelect?: (file: MediaFile | MediaImage) => void;
    fileTypeFilter?: 'all' | 'images' | 'documents';
  }) => void;
  closeMediaDialog: () => void;
}

const MediaDialogContext = createContext<MediaDialogContextType | null>(null);

export const useMediaDialog = () => {
  const context = useContext(MediaDialogContext);
  if (!context) {
    throw new Error('useMediaDialog must be used within a MediaDialogProvider');
  }
  return context;
};

interface MediaDialogProviderProps {
  children: React.ReactNode;
}

export const MediaDialogProvider: React.FC<MediaDialogProviderProps> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [onSelect, setOnSelect] = useState<((file: MediaFile | MediaImage) => void) | undefined>();
  const [fileTypeFilter, setFileTypeFilter] = useState<'all' | 'images' | 'documents'>('all');

  const openMediaDialog = useCallback((options?: {
    onSelect?: (file: MediaFile | MediaImage) => void;
    fileTypeFilter?: 'all' | 'images' | 'documents';
  }) => {
    setOnSelect(() => options?.onSelect);
    setFileTypeFilter(options?.fileTypeFilter || 'all');
    setIsOpen(true);
  }, []);

  const closeMediaDialog = useCallback(() => {
    setIsOpen(false);
    setOnSelect(undefined);
  }, []);

  const handleSelect = useCallback((file: MediaFile | MediaImage) => {
    if (onSelect) {
      onSelect(file);
    }
    closeMediaDialog();
  }, [onSelect, closeMediaDialog]);

  return (
    <MediaDialogContext.Provider value={{ openMediaDialog, closeMediaDialog }}>
      {children}
      <MediaDialog
        open={isOpen}
        onClose={closeMediaDialog}
        onSelect={handleSelect}
        fileTypeFilter={fileTypeFilter}
      />
    </MediaDialogContext.Provider>
  );
}; 